/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-core: tier/status classifiers and screening-summary construction
// (core-viewer.js 219-223, 259-351, 4018-4030, 4038-4054 dropped, 4129-4199).
// energyLocation (4038-4054) is dead in the prototype and dropped.

import { fmtNumber } from "./format"
import { energyStrengthTier } from "./energy"
import { rejectGate, isGateHardRejected, rejectGateMetrics } from "./gate"

export function statusFromSignalQuality(q) {
  if (!q || q.status === "unreliable") return "red"
  if (q.status === "reliable") return "green"
  return "amber"
}

export function confidenceTier(score) {
  if (!Number.isFinite(Number(score))) return { label: "Unreliable", className: "unreliable" }
  if (score >= 0.75) return { label: "High", className: "high" }
  if (score >= 0.45) return { label: "Moderate", className: "moderate" }
  return { label: "Limited", className: "low" }
}

export function signalQualityTier(score, gate = null) {
  if (isGateHardRejected(gate)) return { label: "Unreliable", className: "unreliable" }
  if (!Number.isFinite(Number(score))) return { label: "Unreliable", className: "unreliable" }
  if (score >= 0.78) return { label: "High", className: "high" }
  if (score >= 0.5) return { label: "Moderate", className: "moderate" }
  return { label: "Limited", className: "low" }
}

export function structuralSoundClassification(activity, evidence, quality) {
  if (!activity || !Number.isFinite(Number(activity.sysCyclePct)) || !Number.isFinite(Number(activity.diaCyclePct))) {
    const unphased = quality && quality.murmurBandEnergyEvidence
    if (unphased && unphased.substantial) {
      return {
        status: "amber",
        label: "Additional heart-sound energy observed",
        observed: "Additional heart-sound energy observed",
        maxCyclePct: null,
        dominantPhase: null,
        energyStrength: unphased.strengthTier || "Detected",
        energyValue: Number.isFinite(Number(unphased.strengthValue)) ? Number(unphased.strengthValue) : null,
        phaseStatus: "pending_review",
        rule: "Phase-independent murmur-band Energy was detected; SYS/DIA attribution is withheld pending phase review.",
      }
    }
    return {
      status: "grey",
      label: "Structural sound not assessed",
      observed: "Not assessed",
      maxCyclePct: null,
      dominantPhase: null,
      energyStrength: "N/A",
      energyValue: null,
      rule: "Structural-sound classification requires scored structural-sound guide cycles.",
    }
  }
  const sysPct = Number(activity.sysCyclePct || 0)
  const diaPct = Number(activity.diaCyclePct || 0)
  const sysMean = Number(activity.sysMean)
  const diaMean = Number(activity.diaMean)
  const dominantPhase = sysPct >= diaPct ? "Systolic" : "Diastolic"
  const maxCyclePct = Math.max(sysPct, diaPct)
  const dominantEnergyValue = dominantPhase === "Systolic" ? sysMean : diaMean
  const fallbackEnergyValue = Math.max(Number.isFinite(sysMean) ? sysMean : 0, Number.isFinite(diaMean) ? diaMean : 0)
  const energyValue = Number.isFinite(dominantEnergyValue) ? dominantEnergyValue : fallbackEnergyValue
  const energyStrength = energyStrengthTier(energyValue).label
  let label = "No clear extra sound pattern"
  let status = "green"
  if (maxCyclePct > 75) {
    label = "Clear repeating extra sound pattern"
    status = "red"
  } else if (maxCyclePct >= 50) {
    label = "Possible repeating extra sound pattern"
    status = "amber"
  }
  return {
    status,
    // Upstream's `label` is `observed` with a trailing " sound" rewritten to
    // "-sound pattern"; the v0.9.1 wording has no such tail, so they coincide.
    label: label.replace(/ sound$/, "-sound pattern"),
    observed: label,
    maxCyclePct,
    dominantPhase,
    energyStrength,
    energyValue,
    rule: "Structural Sound status uses cycle repeatability only: <50% Normal, 50-75% Borderline, >75% Elevated. Energy strength is supporting evidence, not a status threshold.",
  }
}

export function statusFromStructural(activity, evidence, quality) {
  return structuralSoundClassification(activity, evidence, quality).status
}

export function screeningStateFromStatus(status, normalLabel = "Normal") {
  if (status === "red") return "Elevated"
  if (status === "green") return normalLabel
  return "Borderline"
}

export function buildScreeningSummary(signalStatus, timingStatus, structuralStatus, recordingName) {
  const elevated = timingStatus === "red" || structuralStatus === "red"
  const borderline = signalStatus !== "green" || timingStatus === "amber" || structuralStatus === "amber"
  const state = elevated ? "Elevated" : borderline ? "Borderline" : "Normal*"
  const basis = []
  if (signalStatus === "green") basis.push("Reliable recording section")
  if (timingStatus === "green") basis.push("Consistent timing guide")
  if (timingStatus === "red") basis.push("Timing pattern for clinician review")
  if (structuralStatus === "green") basis.push("No clear repeating extra sound pattern")
  if (structuralStatus === "amber") basis.push("Possible repeating extra sound pattern")
  if (structuralStatus === "red") basis.push("Clear repeating extra sound pattern")
  return {
    state,
    patientText:
      state === "Elevated"
        ? "This screening found an extra heart-sound pattern in the uploaded recording. Please review it with a healthcare professional."
        : state === "Borderline"
        ? "This screening found a possible extra heart-sound pattern or limited recording quality. Review with clinical context."
        : "Timing and structural-sound screening are within expected range in the usable part of this recording.",
    providerText: `Draft screening summary from Viewer evidence for ${recordingName}: ${state}. Reviewing Clinician validation is required before release.`,
    basis,
    limitations: [
      "Screening result only",
      "Does not rule out heart disease",
      "Single-recording scope: findings apply to the uploaded recording only",
      "Viewer evidence is unreviewed until accepted by the Reviewing Clinician",
    ],
  }
}

export function guideQualityPenalty(payload) {
  const metrics = payload.metrics || {}
  const gateMetrics = rejectGateMetrics(payload)
  const pctHigh = Number(metrics.pct_high_conf ?? gateMetrics.pct_high_conf)
  const orientation = Number(metrics.score ?? gateMetrics.orientation_score)
  // Beat-band caps (upstream v0.9.1): the gate's beat-band measurements, when
  // the pipeline emitted them; guides without them are not capped by them.
  const beatProminence = Number(gateMetrics.beat_band_prominence_db)
  const beatRhythm = Number(gateMetrics.beat_band_rhythm_autocorr_peak)
  const beatShare = Number(gateMetrics.beat_band_share_40_125hz)
  const guideQuality = String(metrics.quality || "").toUpperCase()
  let cap = 1.0
  const reasons = []
  if (Number.isFinite(beatProminence) && beatProminence < 12) {
    cap = Math.min(cap, 0.58)
    reasons.push(`${fmtNumber(beatProminence, 1)} dB beat-band prominence`)
  }
  if (Number.isFinite(beatRhythm) && beatRhythm < 0.38) {
    cap = Math.min(cap, 0.58)
    reasons.push("weak beat-band rhythm")
  }
  if (Number.isFinite(beatShare) && beatShare < 0.14) {
    cap = Math.min(cap, 0.62)
    reasons.push("low beat-band energy share")
  }
  if (guideQuality === "LOW") {
    cap = Math.min(cap, 0.58)
    reasons.push("low S1/S2 guide quality")
  }
  if (Number.isFinite(pctHigh) && pctHigh < 0.35) {
    cap = Math.min(cap, 0.44)
    reasons.push(`${fmtNumber(pctHigh * 100, 0)}% high-confidence markers`)
  } else if (Number.isFinite(pctHigh) && pctHigh < 0.5) {
    cap = Math.min(cap, 0.52)
    reasons.push(`${fmtNumber(pctHigh * 100, 0)}% high-confidence markers`)
  }
  if (Number.isFinite(orientation) && orientation < 0.45) {
    cap = Math.min(cap, 0.44)
    reasons.push("weak S1/S2 orientation score")
  }
  return { cap, reasons, pctHigh, orientation, guideQuality, beatProminence, beatRhythm, beatShare }
}

export function soiTrackConfidence(payload, activity, quality) {
  const gate = rejectGate(payload)
  const guidePenalty = guideQualityPenalty(payload)
  const modelTriage = payload.murmurTriage || (payload.examPackage && payload.examPackage.murmur_triage) || null
  const reviewMode = modelTriage && modelTriage.review_priority_mode
  const confidenceMode =
    modelTriage &&
    (modelTriage.confidence_mode || {
      probability: modelTriage.probability,
      decision_threshold: modelTriage.decision_threshold,
    })
  const rawSignalScore =
    gate && Number.isFinite(Number(gate.score))
      ? Math.max(0, Math.min(1, Number(gate.score)))
      : quality && quality.status === "reliable"
      ? 0.82
      : quality && quality.status === "limited"
      ? 0.58
      : 0.3
  const signalScore = rawSignalScore
  const rawTimingScore =
    quality && quality.timingAllowed ? Math.max(0, Math.min(1, Number(quality.markerConfidence || 0))) : 0
  const timingScore = Math.min(rawTimingScore, guidePenalty.cap)
  const maxRepeat = activity ? Math.max(activity.sysCyclePct || 0, activity.diaCyclePct || 0) / 100 : 0
  const repeatSupport = Math.max(0, Math.min(1, maxRepeat / 0.35))
  const modeSupport = (mode) => {
    if (!mode || !Number.isFinite(Number(mode.probability))) return null
    const threshold = Number.isFinite(Number(mode.decision_threshold)) ? Number(mode.decision_threshold) : 0.29
    return Math.max(0, Math.min(1, Number(mode.probability) / Math.max(0.01, threshold)))
  }
  const reviewSupport = modeSupport(reviewMode)
  const confidenceSupport = modeSupport(confidenceMode)
  const textureSupport =
    reviewSupport !== null ? reviewSupport : confidenceSupport !== null ? confidenceSupport : repeatSupport
  const patternScore =
    quality && quality.timingAllowed
      ? Math.max(0, Math.min(1, 0.45 * timingScore + 0.35 * textureSupport + 0.2 * repeatSupport))
      : Math.max(0, Math.min(0.4, 0.55 * textureSupport))
  const energyScore = Math.max(0, Math.min(1, maxRepeat))
  return {
    signalScore,
    timingScore,
    patternScore,
    energyScore,
    guidePenalty,
    repeatSupport,
    textureSupport,
    reviewProbability:
      reviewMode && Number.isFinite(Number(reviewMode.probability)) ? Number(reviewMode.probability) : null,
    reviewThreshold:
      reviewMode && Number.isFinite(Number(reviewMode.decision_threshold))
        ? Number(reviewMode.decision_threshold)
        : null,
    confidenceProbability:
      confidenceMode && Number.isFinite(Number(confidenceMode.probability)) ? Number(confidenceMode.probability) : null,
    maxRepeat,
    maxRepeatPct: maxRepeat * 100,
  }
}
